import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import CustomerServiceLayoutDefault from '../layout/default'
import Field from '../../../components/field'
import PhoneNumberInput from '../../../components/phone-number-input'
import {Box, Button, Flex} from '@chakra-ui/react'
import Recaptcha from '../../../components/recaptcha'
import {useForm} from 'react-hook-form'
import useContactFormFields from '../../../components/forms/useContactFormFields'
import {API_ERROR_MESSAGE} from '../../../constants'
import {useIntl} from 'react-intl'
import {useCustomerServiceContactMutation} from '../../../hooks/useScapiCustomApi'
import CSHeader from '../partials/header'
import useCustomerServicePageConfig from '../hooks/use-page-config'
import {useToast} from '../../../hooks/use-toast'
import {useCountry} from '../../../hooks'
import Address1AutocompleteInput from '../../../components/place-autocomplete-input'

const CustomerServiceContactFormPage = ({casetypeOptions, producttypeOptions}) => {
    const pageConfig = useCustomerServicePageConfig()
    const {name, description} = pageConfig
    const form = useForm({mode: 'onChange'})
    const fields = useContactFormFields({form, prefix: '', casetypeOptions, producttypeOptions})
    const {formatMessage} = useIntl()
    const toast = useToast()
    const recaptchaRef = useRef(null)
    const {locale} = useIntl()
    const countryCode = useCountry()

    // until commerce-sdk-react@3.10.0 we can't provide the apiOptions to the mutateAsync function
    // and need to use a state and effect to submit the form
    const [apiOptions, setApiOptions] = useState(null)
    const {mutateAsync: postContactForm, isLoading: isPostContactFormLoading} =
        useCustomerServiceContactMutation(apiOptions, {
            onError: (error) => {
                console.error('Error while sending contact form', error)
                toast({
                    title: formatMessage(API_ERROR_MESSAGE),
                    status: 'error'
                })
            },
            onSuccess: () => {
                toast({
                    title: formatMessage({
                        defaultMessage: 'The contact form has been sent successfully.',
                        id: 'customer_service.contactform.toast.success'
                    }),
                    status: 'success'
                })
                form.reset()
            }
        })
    useEffect(() => {
        if (apiOptions) {
            postContactForm(setApiOptions)
            setApiOptions(null)
        }
    }, [apiOptions, postContactForm])

    const submitForm = async (data) => {
        const token = await recaptchaRef.current.execute()
        const body = {
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone,
            email: data.email,
            caseType: data.caseType,
            priority: 'Medium',
            productArea: data.productType,
            subject: data.subject,
            description: data.message,
            stateCode: data.stateCode,
            country: data.countryCode,
            addressStreet: data.address1,
            addressZip: data.postalCode,
            addressCity: data.city
        }
        const parameters = {
            c_gRecaptchaResponse: token
        }

        // this will submit the form on the next render
        setApiOptions({body, parameters})
    }

    // compute a combined loading state for when the form is submitting
    const isLoading =
        // if we are in the submitForm handler
        form.formState.isSubmitting ||
        // if we have a pending api call
        !!apiOptions ||
        // if we are waiting for the api call to finish
        isPostContactFormLoading

    return (
        <CustomerServiceLayoutDefault>
            <CSHeader headline={name} subline={description}></CSHeader>
            <form onSubmit={form.handleSubmit(submitForm)} noValidate>
                <Box backgroundColor="stokkeGray.light">
                    {countryCode !== 'JP' && (
                        <Box padding={4}>
                            <Field {...fields.salutation} />
                        </Box>
                    )}
                    <Flex padding={4} gap={4}>
                        <Field {...fields.firstName} />
                        <Field {...fields.lastName} />
                    </Flex>
                    <Box padding={4}>
                        <Field {...fields.email} />
                    </Box>
                    <Box padding={4}>
                        <Field {...fields.confirmEmail} />
                    </Box>
                    <Box padding={4}>
                        <Address1AutocompleteInput form={form} fieldProps={fields.address1} />
                    </Box>
                    <Flex padding={4} gap={4}>
                        <Field {...fields.city} />
                        {(locale === 'ja-JP' ||
                            locale === 'en-US' ||
                            locale === 'en-CA' ||
                            locale === 'fr-CA') && <Field {...fields.stateCode} />}
                        <Field {...fields.countryCode} />
                    </Flex>
                    <Flex padding={4} gap={4}>
                        <Field {...fields.postalCode} />
                        <PhoneNumberInput {...fields.phone} />
                    </Flex>
                    <Flex padding={4} gap={4}>
                        <Field {...fields.caseType} />
                        <Field {...fields.productType} />
                    </Flex>
                    <Box padding={4}>
                        <Field {...fields.subject} />
                    </Box>
                    <Box padding={4}>
                        <Field {...fields.message} />
                    </Box>
                    <Box padding={4}>
                        <Recaptcha ref={recaptchaRef} />
                    </Box>
                    <Box padding={4}>
                        <Button
                            type="submit"
                            width="full"
                            onClick={() => {
                                form.clearErrors('global')
                                // todo trackEvent
                            }}
                            isLoading={isLoading}
                        >
                            {formatMessage({
                                defaultMessage: 'Submit',
                                id: 'global.submit'
                            })}
                        </Button>
                    </Box>
                </Box>
            </form>
        </CustomerServiceLayoutDefault>
    )
}

CustomerServiceContactFormPage.propTypes = {
    casetypeOptions: PropTypes.array,
    producttypeOptions: PropTypes.array
}

export default CustomerServiceContactFormPage
